/**
 * AssetDetail (C2.4) — eingebetteter, PROP-getriebener Jahres-Drilldown einer
 * Gesellschaft. Ein Rendering-Pfad: PortfolioPage (Scroll-Cockpit) und der
 * Route-Wrapper /asset/:short reichen dasselbe Entity-Objekt durch.
 *
 * Inhalt: (a) Jahresvergleich (inkl. FCF/FCFE, R2-Punkt 4), (b) Cashflow +
 * Wasserfall + FCF/FCFE-Quartale + klassifizierte Bestände (R2-Punkt 4),
 * Kommentare & Hinweise (R2-Punkt 5), (c) RoE (VJ + Prognose „geschätzt"),
 * (d) Darlehensentwicklung (BoY/Q1 + EoP-Summe), (e) Bewertung (nur falls
 * vorhanden). Dazu Bestand: Soll/Ist Q1, BWA-Drilldown, Aktivierungen
 * (R2-Punkt 3), Intercompany.
 *
 * HARTE null-Regeln (SCHEMA_MAPPING §5): null-IST nie als 0 → "—"; bewertung
 * null → Karte ausblenden; loans [] → „keine Bankdarlehen".
 */
import { BWA_LABELS } from '../types/snapshot';
import type { BwaKey, CashflowBlock, Entity, QuarterKey } from '../types/snapshot';
import { KpiCard } from './KpiCard';
import { QuarterStatusBadge } from './QuarterStatusBadge';
import { BwaTable } from './BwaTable';
import { CashflowWaterfall } from './CashflowWaterfall';
import { cashflowWaterfall } from '../lib/waterfall';
import { computeAbweichung, DASH, formatEur, formatValue } from '../lib/format';
import { RetroPanel } from './RetroPanel';
import { quartalsGrenzRetros } from '../lib/retro';
import { CashflowFcfe } from './CashflowFcfe';
import { HinweisePanel } from './HinweisePanel';
import { Aktivierungen } from './Aktivierungen';
import { IntercompanyTabelle } from './IntercompanyTabelle';
import { KiAnalysePanel } from './KiAnalyse';
import { useJahre } from '../lib/jahre';
import { aktivierungenAusblenden, ausgeblendeteImmoZeilen } from '../lib/immoZeilen';

const JAHR_KEYS: { key: BwaKey; label: string }[] = [
  { key: 'nkm', label: 'NKM' },
  { key: 'evv', label: 'EvV' },
  { key: 'ebtda', label: 'EBTDA' },
  { key: 'ju', label: 'JÜ' },
];

const CASHFLOW_ROWS: { key: keyof CashflowBlock; label: string }[] = [
  { key: 'ebtda', label: 'EBTDA' },
  { key: 'tilgung', label: 'Tilgung' },
  { key: 'gd_change', label: 'GD-Veränderung' },
  { key: 'netto_cf', label: 'Netto-Cashflow' },
  // A-4: „Cash (Ende)" entfernt — cash_end existiert laut Contract nur in q1_ytd
  // (snapshot.ts), stand in der VJ- und Prognose-Spalte also zwangsläufig auf „—".
  // Der Snapshot liefert das Feld weiter; D-039/K2-Rollforward bleibt unberührt.
];

/** Quartalsspalten Q1–Q4 (Schema 2.0). Jede Spalte trägt ihren Status-Badge. */
const QUARTER_COLS: { key: QuarterKey; label: string }[] = [
  { key: 'q1_2026', label: 'Q1' },
  { key: 'q2_2026', label: 'Q2' },
  { key: 'q3_2026', label: 'Q3' },
  { key: 'q4_2026', label: 'Q4' },
];

/** Ergebnis-KPIs der Soll/Ist-Quartalsmatrix (wie zuvor: NKM, EBTDA, JÜ). */
const SOLL_IST_KEYS: { key: BwaKey; label: string }[] = [
  { key: 'nkm', label: 'NKM' },
  { key: 'ebtda', label: 'EBTDA' },
  { key: 'ju', label: 'JÜ' },
];

export interface AssetDetailProps {
  entity: Entity;
}

export function AssetDetail({ entity }: AssetDetailProps) {
  // D-089: Jahreszahlen der Beschriftungen aus meta. Die Schema-Schlüssel
  // (vorjahr_2025, jahr_2026, q1_2026 …) bleiben Vertrag und bedeuten VJ bzw. BJ.
  const { bj: berichtsjahr, vj: vorjahr } = useJahre();
  const vj = entity.vorjahr_2025;
  const jahrIst = entity.jahr_2026.ist;
  const prognose = entity.jahr_2026.prognose;
  const roe = entity.equity_roe;
  const bewertung = entity.bewertung;
  const cf = entity.cashflow;
  const q1 = entity.quarterly.q1_2026;
  // R2 Phase 4b: FCFE-Jahresbausteine (Jahresvergleich) + Bestände (klassifiziert).
  const fcfeJahr = entity.fcfe?.jahr;
  const bestaende = entity.bank_bestaende;

  // D-041: YTD-Cashflow (reine Ist-Anteile) mit dynamischem Label; Fallback auf q1_ytd ("Q1").
  const ytdBlock = cf.ytd ?? cf.q1_ytd;
  const ytdLabel = cf.ytd ? `YTD ${cf.ytd.stichtag}` : 'Q1';

  // D-041: BWA-Drilldown über ABGESCHLOSSENE Quartale (Single-Source aus dem Export).
  // Heute nur Q1 closed → Anzeige inhaltlich unverändert; Titel dynamisch aus den Quartalen.
  const kum = entity.quarterly_kumuliert;
  const drilldownQuarter = kum
    ? { status: 'closed' as const, ist: kum.ist, soll: kum.soll,
        abweichung_pct: kum.abweichung_pct, abweichung_guenstig_pct: kum.abweichung_guenstig_pct }
    : q1;
  const qLabels = (kum?.quartale ?? ['q1_2026']).map((q) => q.replace('_2026', '').toUpperCase());
  const drilldownTitel =
    qLabels.length > 1
      ? `BWA-Drilldown (${qLabels[0]}–${qLabels[qLabels.length - 1]} ${berichtsjahr} kumuliert)`
      : `BWA-Drilldown (${qLabels[0] ?? 'Q1'} ${berichtsjahr})`;
  const { loans, gd_boy, bank_boy } = entity.darlehen;

  // Prompt 128 §1: bei Nicht-Mitgliedern durchgehend leere Immobilienzeilen ausblenden
  // (nur Null, nie ein Wert ≠ 0). Für Portfolio-Mitglieder immer leer → unverändert.
  const ausblenden = ausgeblendeteImmoZeilen(entity);
  const aktAusblenden = aktivierungenAusblenden(entity);
  const jahrKeys = JAHR_KEYS.filter(({ key }) => !ausblenden.has(key));
  const sollIstKeys = SOLL_IST_KEYS.filter(({ key }) => !ausblenden.has(key));
  const ausgeblendetText = [
    ...[...ausblenden].map((k) => BWA_LABELS[k]),
    ...(aktAusblenden ? ['Aktivierungen'] : []),
  ].join(', ');

  // D-036 (5b): sichere Quartalsgrenz-Retros je Quartal aggregieren (R1, konservativ).
  const grenzMap: Partial<Record<QuarterKey, number>> = {};
  for (const g of quartalsGrenzRetros(entity.retro_hinweise)) {
    grenzMap[g.quartal] = (grenzMap[g.quartal] ?? 0) + g.betrag;
  }

  // (d) EoP-Prognose NUR auf Gesellschaftsebene (aus Cashflow-Prognose).
  const hasBank = loans.length > 0 || (bank_boy ?? 0) > 0;
  const hasGd = (gd_boy ?? 0) > 0;
  const bankEoP = (bank_boy ?? 0) - Math.abs(cf.prognose.tilgung ?? 0);
  const gdEoP = (gd_boy ?? 0) + (cf.prognose.gd_change ?? 0);
  const boySum = (hasBank ? (bank_boy ?? 0) : 0) + (hasGd ? (gd_boy ?? 0) : 0);
  const eopSum = (hasBank ? bankEoP : 0) + (hasGd ? gdEoP : 0);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-text">{entity.name}</h2>
        <p className="text-sm text-text-muted">
          {entity.klasse} · {entity.short_name}
        </p>
        {/* Ausweisen statt still weglassen (D-075-Muster): der Leser sieht, WAS fehlt
            und warum — sonst wirkte eine fehlende NKM-Zeile wie ein Datenfehler. */}
        {ausgeblendetText && (
          <p className="mt-1 text-xs text-text-muted">
            Nicht immobilienhaltend — durchgehend 0 und deshalb ausgeblendet:{' '}
            {ausgeblendetText}.
          </p>
        )}
      </div>

      {/* (a) Jahresvergleich */}
      <section aria-label={`Jahresvergleich ${vorjahr}/${berichtsjahr}`}>
        <h3 className="mb-3 text-lg font-semibold text-text">Jahresvergleich</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th scope="col" className="px-4 py-2 font-medium">Position</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">VJ {vorjahr}</th>
                {/* A-3 Lesart (B): die Spalte ist KEIN Ganzjahreswert, sondern die
                    Summe der aktiven Quartale. Das steht jetzt in der Beschriftung
                    selbst, nicht nur in der Fußnote. */}
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Jahres-Ist (Σ aktive Quartale)*
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">Prognose {berichtsjahr}</th>
                <th scope="col" className="px-4 py-2 text-right font-medium">Δ vs VJ</th>
              </tr>
            </thead>
            <tbody>
              {jahrKeys.map(({ key, label }) => (
                <tr key={key} className="border-b border-border last:border-0">
                  <th scope="row" className="px-4 py-2 text-left font-normal">{label}</th>
                  <td className="px-4 py-2 text-right tabular-nums">{formatEur(vj[key])}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-text-muted">
                    {formatEur(jahrIst[key])}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatEur(prognose[key])}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatValue(computeAbweichung(prognose[key], vj[key]), { kind: 'percent' })}
                  </td>
                </tr>
              ))}
              {/* R2-Punkt 4: FCF/FCFE aufgenommen. Kein VJ/Δ (fcfe.jahr hat nur ist/prognose). */}
              {fcfeJahr &&
                ([
                  { ln: 'fcf', label: 'FCF' },
                  { ln: 'fcfe', label: 'FCFE' },
                ] as const).map(({ ln, label }) => (
                  <tr key={ln} className="border-b border-border last:border-0 bg-surface-2/40">
                    <th scope="row" className="px-4 py-2 text-left font-normal">{label}</th>
                    <td className="px-4 py-2 text-right tabular-nums text-text-muted">{DASH}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-text-muted">
                      {formatEur(fcfeJahr[ln].ist)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatEur(fcfeJahr[ln].prognose)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-text-muted">{DASH}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {/* A-3: Wortlaut an Legende A51 der QR-Mappe angelehnt (lbu-standortreporting/
            scripts/lib/qr_gen.py, LEGENDE_ZEILEN[51]), damit Dashboard und Excel
            dieselbe Systematik beschreiben. „OHNE Hochrechnung auf 12 Monate"
            (Jahresebene) und „Restmonate hochgerechnet" (Quartalsebene) sind
            getrennt benannt — zusammengezogen lasen sie sich widersprüchlich. */}
        <p className="mt-2 text-xs text-text-muted">
          *Jahres-Ist = Summe der aktiven Quartale (abgeschlossene + laufendes),
          OHNE Hochrechnung auf 12 Monate — künftige Quartale sind NICHT enthalten;
          im laufenden Quartal sind lediglich die Restmonate hochgerechnet.
          Jahres-Prognose = aktive Quartale + künftige Quartale zum Soll
          (R4-Mix, D-031). FCF/FCFE ohne VJ/Δ (nur Ist + Prognose im Snapshot).
        </p>
      </section>

      {/* (c) RoE: VJ + Prognose */}
      <section aria-label="RoE Eigenkapitalrendite">
        <h3 className="mb-3 text-lg font-semibold text-text">RoE (Eigenkapitalrendite)</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KpiCard
            label={`RoE (VJ ${vorjahr})`}
            value={roe.vj.roe_pct}
            kind="percent"
            footer={`RoE (EUR): ${formatEur(roe.vj.roe_eur)}`}
          />
          <KpiCard
            label={`RoE (Prognose ${berichtsjahr})`}
            value={roe.prognose.roe_pct}
            kind="percent"
            estimated
            footer={`RoE (EUR): ${formatEur(roe.prognose.roe_eur)}`}
          />
        </div>
      </section>

      {/* (e) Bewertung. D-075: `bewertung: null` heißt „nicht anwendbar"
          (valuation_applicable = false) und wird als solches AUSGEWIESEN — nicht
          still weggelassen und niemals als 0. Vorher verschwand der Block
          geräuschlos; der Leser konnte nicht unterscheiden, ob die Bewertung fehlt
          oder ob es sie nicht gibt. */}
      <section aria-label="Bewertung / NAV">
        <h3 className="mb-3 text-lg font-semibold text-text">Bewertung / NAV</h3>
        {bewertung ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard label="NAV (VJ)" value={bewertung.vj.nav} />
            <KpiCard label="NAV (Prognose)" value={bewertung.prognose.nav} estimated />
            <KpiCard label="GAV (12,5×, VJ)" value={bewertung.vj.value_125} />
          </div>
        ) : (
          <p className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">
            Nicht anwendbar
            {entity.valuation_applicable === false
              ? ' — für diese Gesellschaft ist keine Multiplikator-Bewertung vorgesehen'
              : ''}
            {/* Tag 21e: Der Satz zitierte hier einen Euro-Betrag, um zu erklären,
                dass keiner gezeigt wird. Ein ZITIERTER Betrag ist von einem
                DARGESTELLTEN maschinell nicht zu unterscheiden — die Prüfung
                „diese Region trägt keinen Betrag" schlug daran an, und ein
                Screenreader liest ihn wie einen Wert vor. Aussage unverändert,
                nur ohne Zahl. */}
            . Es wird bewusst kein Wert ausgewiesen; eine ausgewiesene Null wäre
            eine Falschaussage.
          </p>
        )}
      </section>

      {/* (b) Cashflow + Wasserfall */}
      <section aria-label="Cashflow">
        <h3 className="mb-3 text-lg font-semibold text-text">Cashflow</h3>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="overflow-x-auto rounded-lg border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th scope="col" className="px-4 py-2 font-medium">Position</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">VJ {vorjahr}</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">{ytdLabel}</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    Prognose{' '}
                    <span aria-hidden="true" className="font-normal text-text-muted" title="Vorläufig – siehe Fußnote (D-030)">‡</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {CASHFLOW_ROWS.map(({ key, label }) => (
                  <tr key={key} className="border-b border-border last:border-0">
                    <th scope="row" className="px-4 py-2 text-left font-normal">{label}</th>
                    <td className="px-4 py-2 text-right tabular-nums">{formatEur(cf.vj[key])}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatEur(ytdBlock[key])}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatEur(cf.prognose[key])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-lg border border-border bg-surface p-4">
            <p className="mb-2 text-sm font-medium text-text-muted">Wasserfall Prognose</p>
            <CashflowWaterfall steps={cashflowWaterfall(cf.prognose)} />
          </div>
        </div>
        <p className="mt-2 text-xs text-text-muted">
          ‡ Cashflow-Prognose vorläufig: Jahres-Tilgung nach R4-Mix (Ist der aktiven
          Quartale zzgl. Hochrechnung), nicht mehr durch Q1-Annualisierung. Gegen
          Darlehensspiegel noch ungeprüft (D-030) — Verfeinerung, sobald
          Darlehensspiegel/GD-Bewegungsdaten vorliegen.
        </p>

        {/* R2-Punkt 4: FCF/FCFE je Quartal (status/basis, Prognose-Kennzeichnung, r003) */}
        <div className="mt-4">
          <CashflowFcfe fcfe={entity.fcfe} />
        </div>

        {/* R2-Punkt 4: klassifizierte Bestände (Bank frei, Kaution/Capex gebunden) */}
        {bestaende && (
          <section className="mt-4 rounded-lg border border-border bg-surface p-4" aria-label="Bestände (klassifiziert)">
            <p className="mb-2 text-sm font-medium text-text-muted">
              Bestände zum Stichtag {bestaende.stichtag}
            </p>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-text-muted">
                  Bank <span className="text-ampel-green">· frei</span>
                </dt>
                <dd className="tabular-nums text-text">{formatEur(bestaende.bank)}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-muted">Kaution · gebunden</dt>
                <dd className="tabular-nums text-text">{formatEur(bestaende.kaution)}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-muted">Capex · gebunden</dt>
                <dd className="tabular-nums text-text">{formatEur(bestaende.capex)}</dd>
              </div>
            </dl>
            {bestaende.unklar_konten.length > 0 && (
              <p className="mt-2 text-xs text-text-muted">
                Nicht eindeutig klassifizierte 18xx-Konten:{' '}
                {bestaende.unklar_konten.join(', ')}.
              </p>
            )}
          </section>
        )}
      </section>

      {/* R2-Punkt 5: Kommentare & Hinweise — UNTER dem Cashflow-Bereich */}
      <HinweisePanel hinweise={entity.hinweise} />

      {/* (d) Darlehensentwicklung */}
      <section aria-label="Darlehen">
        <h3 className="mb-3 text-lg font-semibold text-text">Darlehensentwicklung</h3>
        <div className="space-y-4 rounded-lg border border-border bg-surface p-4">
          {loans.length === 0 ? (
            <p className="text-sm italic text-text-muted">keine Bankdarlehen</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th scope="col" className="py-2 font-medium">Darlehen</th>
                  <th scope="col" className="py-2 text-right font-medium">BoY</th>
                  <th scope="col" className="py-2 text-right font-medium">Tilgung Q1-Ist</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan, i) => (
                  <tr key={`${loan.label}-${i}`} className="border-b border-border/60">
                    <th scope="row" className="py-1.5 text-left font-normal">{loan.label}</th>
                    <td className="py-1.5 text-right tabular-nums">{formatEur(loan.boy)}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatEur(loan.tilgung)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* GD-Zeile + EoP-Prognose-Summe (nur Gesellschaftsebene) */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th scope="col" className="py-2 font-medium">EoP-Prognose (Summe, aus Cashflow-Prognose)</th>
                <th scope="col" className="py-2 text-right font-medium">BoY</th>
                <th scope="col" className="py-2 text-right font-medium">EoP-Prognose</th>
              </tr>
            </thead>
            <tbody>
              {hasBank && (
                <tr className="border-b border-border/60">
                  <th scope="row" className="py-1.5 text-left font-normal">Bank gesamt</th>
                  <td className="py-1.5 text-right tabular-nums">{formatEur(bank_boy)}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatEur(bankEoP)}</td>
                </tr>
              )}
              {hasGd && (
                <tr className="border-b border-border/60">
                  <th scope="row" className="py-1.5 text-left font-normal">Gesellschafterdarlehen</th>
                  <td className="py-1.5 text-right tabular-nums">{formatEur(gd_boy)}</td>
                  <td className="py-1.5 text-right tabular-nums">{formatEur(gdEoP)}</td>
                </tr>
              )}
              <tr className="font-medium">
                <th scope="row" className="py-1.5 text-left">Entschuldung (informativ)</th>
                <td className="py-1.5 text-right tabular-nums">{formatEur(boySum)}</td>
                <td className="py-1.5 text-right tabular-nums">{formatEur(boySum - eopSum)}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-text-muted">
            EoP-Prognose vorläufig — abgeleitet aus der vorläufigen Cashflow-Tilgung
            (Jahres-Tilgung nach R4-Mix, D-030).
          </p>
        </div>
      </section>

      {/* Bestand: Soll/Ist je Quartal (Schema 2.0 — Quartalsspalten mit Status statt Q1-only) */}
      <section aria-label={`Soll/Ist ${berichtsjahr} (Quartale)`}>
        <h3 className="mb-3 text-lg font-semibold text-text">Soll/Ist {berichtsjahr} (Quartale)</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th scope="col" className="px-4 py-2 font-medium">Position</th>
                {QUARTER_COLS.map(({ key, label }) => {
                  const q = entity.quarterly[key];
                  return (
                    <th key={key} scope="col" className="px-4 py-2 text-right font-medium">
                      <div className="flex flex-col items-end gap-1">
                        <span>{label}</span>
                        {q ? (
                          <QuarterStatusBadge status={q.status} />
                        ) : (
                          <span className="text-xs font-normal italic text-text-muted">{DASH}</span>
                        )}
                        {grenzMap[key] != null && (
                          <span
                            title="Im Quartals-Erstmonat ausgewiesene Nachbuchung aus dem Vorquartal (D-036, Kennzeichnung). Konservativ: nur sichere Grenzfälle (Ausweismonat = Quartals-Erstmonat)."
                            className="rounded border border-border bg-surface-2 px-1 text-[10px] font-normal text-text-muted"
                          >
                            Vorquartals-Nachbuchung {formatEur(grenzMap[key]!)}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sollIstKeys.map(({ key, label }) => (
                <tr key={key} className="border-b border-border last:border-0">
                  <th scope="row" className="px-4 py-2 text-left font-normal">{label}</th>
                  {QUARTER_COLS.map(({ key: qk }) => {
                    const q = entity.quarterly[qk];
                    return (
                      <td key={qk} className="px-4 py-2 text-right tabular-nums">
                        {q ? (
                          <div className="flex flex-col items-end">
                            <span>{formatEur(q.ist[key])}</span>
                            <span className="text-xs text-text-muted">
                              Soll {formatEur(q.soll[key])}
                            </span>
                          </div>
                        ) : (
                          <span className="text-text-muted">{DASH}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-text-muted">
          Ist je Quartal gemäß Status-Badge (R4-Mix): „Abgeschlossen" = Quartals-SuSa
          vollständig, „Laufend" = Ist-Monate + Hochrechnung, „Prognose" = Q-Soll.
          „{DASH}" = Quartal noch ohne Snapshot.
        </p>
      </section>

      {/* D-036: Nachbuchungen & Prüfhinweise (Kennzeichnen statt Korrigieren) */}
      <RetroPanel
        retroHinweise={entity.retro_hinweise}
        pruefluecken={entity.pruefluecken}
        afaWarnung={entity.afa_warnung}
      />

      {/* D-084 (Prompt 127): KI-Analyse direkt VOR der BWA-Auswertung. Ohne Feld
          (alter Snapshot) rendert die Komponente nichts. */}
      <KiAnalysePanel analyse={entity.ki_analyse} />

      {/* Bestand: BWA-Drilldown über abgeschlossene Quartale (D-041) */}
      <section aria-label="BWA-Drilldown">
        <h3 className="mb-3 text-lg font-semibold text-text">{drilldownTitel}</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-surface p-4">
          <BwaTable quarter={drilldownQuarter} ausblenden={ausblenden} />
        </div>
      </section>

      {/* R2-Punkt 3: Aktivierungen (Anlagevermögen) — UNTER dem BWA-Drilldown.
          A-6/A-8: der Stichtag für den „Σ Ist-Monate"-Kopf kommt aus dem Snapshot
          (cashflow.ytd.stichtag), nicht aus einem hartkodierten Monat. */}
      {!aktAusblenden && (
        <Aktivierungen aktivierungen={entity.aktivierungen} stichtag={cf.ytd?.stichtag} />
      )}

      {/* Bestand: Intercompany. A-9 / D-072: eigene Komponente, weil die Tabelle
          jetzt Quartals-IST, Status-Badges, Unvollständigkeits-Markierung und die
          fehlt_2026-Kennzeichnung trägt — dem Muster der übrigen Blöcke folgend
          (CashflowFcfe, Aktivierungen, RetroPanel, HinweisePanel). */}
      <IntercompanyTabelle entity={entity} />
    </div>
  );
}
